/**
 * useEditorBridge
 *
 * Connects the VBECS editor to pulse-api via Aldoo Realtime (socket.io).
 * - Pushes editor state to the server on change (debounced)
 * - Receives editor:command events and applies them to local node state
 */

import { useEffect, useRef, useCallback } from 'react';
import { useRealtime } from 'aldoo-ra/Realtime/hooks.js';
import Value from 'aldoo-ra/Value';

const NAMESPACE = 'vbecs';
const PUSH_DEBOUNCE_MS = 200;

const applyCommand = (msg, setNodes, setSelectedNodeIds) => {
  const { type, payload } = msg || {};
  switch (type) {
    case 'UPDATE_NODE': {
      const { nodeId, props, attachedComponents, nodeProps } = payload;
      setNodes(prev =>
        prev.map(n => {
          if (n.id !== nodeId) return n;
          const updated = { ...n, ...nodeProps };
          if (props) updated.props = { ...n.props, ...props };
          if (attachedComponents) updated.attachedComponents = attachedComponents;
          return updated;
        })
      );
      break;
    }
    case 'ADD_NODE': {
      const { componentId, parentId, props = {}, position } = payload;
      setNodes(prev => [...prev, {
        id: `node-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        type: 'component',
        componentId,
        props,
        parentId: parentId || null,
        position: position || { x: 100, y: 100 },
        size: { width: 200, height: 100 },
        children: [],
      }]);
      break;
    }
    case 'REMOVE_NODE': {
      setNodes(prev => prev.filter(n => n.id !== payload.nodeId));
      break;
    }
    case 'SELECT_NODE': {
      setSelectedNodeIds([payload.nodeId]);
      break;
    }
    case 'SET_NODES': {
      setNodes(payload.nodes);
      break;
    }
    default:
      break;
  }
};

export function useEditorBridge({ nodes, selectedNodeIds, page, setNodes, setSelectedNodeIds }) {
  const [auth] = Value('user-credentials');
  const { socket, connected } = useRealtime(auth ? NAMESPACE : null);
  const debounceRef = useRef(null);

  const handleCommand = useCallback((msg) => {
    applyCommand(msg, setNodes, setSelectedNodeIds);
  }, [setNodes, setSelectedNodeIds]);

  // Receive commands from MCP tools via pulse-api
  useEffect(() => {
    if (!socket) return;
    socket.on('editor:command', handleCommand);
    return () => socket.off('editor:command', handleCommand);
  }, [socket, handleCommand]);

  // Push state to pulse-api so MCP tools can read it
  useEffect(() => {
    if (!connected || !socket) return;

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const state = {
        nodes,
        selectedNodeIds: Array.from(selectedNodeIds || []),
        page: page ? { _id: page._id, name: page.content?.name || page.name } : null,
        updatedAt: Date.now(),
      };
      socket.emit('editor:state', state);
    }, PUSH_DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
  }, [nodes, selectedNodeIds, page, connected, socket]);
}
